import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Modal from 'react-modal';

import { pluralize, stripSpecialChars } from 'helpers';
import MelodyCardProduct from 'components/common/melodyCard/MelodyCardProduct';
import css from 'styles/components/productdetail/outOfStockModal.scss';
import ProductUtils from 'helpers/ProductUtils';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { trackEvent } from 'helpers/analytics';

/**
 * Zappos out of stock modal
 */
class OutOfStockModal extends Component {
  state = {
    visible: null
  };

  componentDidUpdate(prevProps) {
    const { selectedSizing: prevSelectedSizing, style: prevStyle } = prevProps;
    const { selectedSizing, style } = this.props;
    const isSizeChanged = prevSelectedSizing !== selectedSizing;
    const isStyleChanged = prevStyle.styleId !== style.styleId;
    if (isSizeChanged || isStyleChanged) {
      this.setState({ visible: null });
    }
  }

  closeModal = () => {
    this.setState({ visible: false });
  };

  onRequestClose = () => {
    this.setState({ visible: false });
  };

  onAfterOpen = () => {
    this.setState({ visible: true });
  };

  makeSelectedSizing = sizeId => {
    const { selectedSizing, sizing: { dimensions } } = this.props;
    const sizingMap = {};

    dimensions.forEach(({ id, rank }) => {
      const dimId = `d${id}`;

      if (rank === '1') {
        sizingMap[dimId] = sizeId;
      } else {
        sizingMap[dimId] = selectedSizing[dimId];
      }
    });

    return sizingMap;
  };

  makeTrackEvent = colorId => () => trackEvent('TE_PDP_SELECTCOLOR', colorId)

  makeAltList = sizeId => {
    const { productStyles, sizing } = this.props;
    const { stockData } = sizing;
    const newSelectedSizing = this.makeSelectedSizing(sizeId);
    const colorsBySize = ProductUtils.getColorsBySize(stockData, newSelectedSizing);
    // Render sizing values only if there are available colors in stock
    if (colorsBySize.length) {

      // Find styleIds that have a color match with colorsBySize
      const matches = Object.keys(productStyles).filter(styleId => {
        const style = productStyles[styleId];// gets whole obj
        return (colorsBySize.find(obj => obj.color === style.colorId));
      });
      return (
        <>
          {matches.map(styleId => {
            const style = productStyles[styleId];
            const { colorId, price, images, color } = style;
            const mainImage = images.find(imgs => imgs.type === 'PAIR' || imgs.type === 'MAIN');
            const imageId = mainImage ? mainImage.imageId : null;
            const msaImageParams = { height: 83, width: 110, autoCrop: true };
            const seoUrl = buildSeoProductUrl(this.props.detail, colorId);
            const cardData = ({ link: seoUrl, price, colorId, imageId, colorName: color, alt: color });

            return <MelodyCardProduct
              key={`${stripSpecialChars(imageId)}_${styleId}`}
              cardData={cardData}
              componentStyle="oos"
              melodyCardTestId="OOS"
              msaImageParams={msaImageParams}
              onComponentClick={this.makeTrackEvent(colorId)}
            />;
          })}
        </>
      );
    } else {
      return null;
    }
  };

  makeAltCombinations = () => {
    const { selectedSizing, sizing } = this.props;
    const { dimensions } = sizing;
    if (!dimensions.length) {
      return null;
    }
    const { id, rank } = dimensions[0];
    const dimId = `d${id}`;
    let currentSizedId;

    if (rank === '1') {
      currentSizedId = selectedSizing[dimId];
    }

    const chosenSizeValue = this.makeAltList(currentSizedId, dimId);
    if (chosenSizeValue) {
      const count = chosenSizeValue.props.children.length;
      return (
        <>
          <p className={css.count}>View {count} available {pluralize('color', count)} in your size</p>
          <div className={css.otherColors}>{chosenSizeValue}</div>
        </>);
    } else {
      return null;
    }
  };

  render() {
    const { visible } = this.state;
    const { selectedSizing, sizing, style: { colorId }, isProductNotifyOpen, onOpenProductNotifyMe, hydraOosm } = this.props;
    const { testId } = this.context;
    const { dimensions } = sizing;
    const allDimensionsSelected = dimensions.every(dim => selectedSizing[`d${dim.id}`]);

    if (!allDimensionsSelected) {
      return null;
    }

    const stock = ProductUtils.getStockBySize(sizing.stockData, colorId, selectedSizing);
    const isVisible = visible !== null ? visible && !isProductNotifyOpen : !stock;

    return (
      <Modal
        isOpen={isVisible}
        onAfterOpen={this.onAfterOpen}
        onRequestClose={this.onRequestClose}
        portalClassName={css.modalPortal}
        overlayClassName={css.modalOverlay}
        className={css.modalContent}
        contentLabel="OutOfStockModal"
        bodyOpenClassName={cn(css.modalOpenBodyStyle, { [css.hydraOosm]: hydraOosm })}>
        <div className={css.oosHeader}>
          <button
            type="button"
            className={css.closeBtn}
            onClick={this.closeModal}
            aria-label="close"
            data-test-id={testId('close')}>
            <span className={css.melodyCloseImg}/>
          </button>
          <h1 className={css.oosTitle}>Out of Stock</h1>
        </div>
        <div className={css.oosNotify}>
          <button type="button" onClick={onOpenProductNotifyMe} data-test-id={testId('notifyMeOutOfStock')}>Notify me when this is back in stock!</button>
        </div>
        {this.makeAltCombinations()}
      </Modal>
    );
  }
}

OutOfStockModal.propTypes = {
  productId: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired,
  sizing: PropTypes.object.isRequired,
  selectedSizing: PropTypes.object,
  productStyles: PropTypes.object.isRequired,
  thumbnails: PropTypes.array.isRequired,
  onStockChange: PropTypes.func.isRequired,
  onSwatchStyleChosen: PropTypes.func.isRequired,
  isProductNotifyOpen: PropTypes.bool,
  onOpenProductNotifyMe: PropTypes.func.isRequired
};

OutOfStockModal.contextTypes = {
  testId: PropTypes.func.isRequired
};

export default OutOfStockModal;
